/**
 * Trip Replay Service for FlowRide
 * Replays recorded trips at exact capture rate
 */

import axios from "axios";
import { estimateJsonUtf8Bytes } from "../../tools/MemoryEstimate.tools";
import { ReplayState, type RecordedTripData } from "../../types/TripRecording";
import { rawCompassDataService } from "./RawCompassDataService";
import { rawGpsDataService } from "./RawGpsDataService";

/**
 * Service that manages trip replay
 */
export class TripReplayService {

    /** Singleton instance */
    private static instance: TripReplayService | null = null;

    /** Current replay state */
    private state: ReplayState = ReplayState.STOPPED;

    /** Current trip data being replayed */
    private currentTrip: RecordedTripData | null = null;

    /** Current GPS data index */
    private currentGpsIndex: number = 0;

    /** Current compass data index */
    private currentCompassIndex: number = 0;

    /** GPS data replay interval ID */
    private gpsIntervalId: number | null = null;

    /** Compass data replay interval ID */
    private compassIntervalId: number | null = null;

    // GPS playing variable
    private gpsPlaying: boolean = false;

    // Compass Playing variable
    private compassPlaying: boolean = false;

    /**
     * Private constructor for singleton pattern
     */
    private constructor() {
        // Private constructor
    }

    /**
     * Get singleton instance
     * @returns Service instance
     */
    public static getInstance(): TripReplayService {
        if ( TripReplayService.instance === null ) {
            TripReplayService.instance = new TripReplayService();
        }

        return TripReplayService.instance;
    }

    /**
     * Load trip data from backend
     * @param tripId - Trip ID to load
     * @returns Promise that resolves when trip is loaded
     */
    public async loadTrip( tripId: number ): Promise<RecordedTripData | null> {
        try {
            const response = await axios.get( `/api/trip-recording/${ tripId }` );
            const tripData = response.data as RecordedTripData;

            this.currentTrip = tripData;
            console.log( "[TripReplayService] Trip loaded", tripData.metadata );

            return tripData;
        } catch( error ) {
            console.error( "[TripReplayService] Error loading trip:", error );

            return null;
        }
    }

    /**
     * Start replaying trip
     * @param tripData - Trip data to replay (optional, uses current trip if not provided)
     */
    public start( tripData?: RecordedTripData ): void {
        if ( this.state === ReplayState.PLAYING ) {
            console.warn( "[TripReplayService] Already playing" );

            return;
        }

        // Pause GPS service watch so that position is not reset to current while streaming replay
        rawGpsDataService.pause();
        rawCompassDataService.pause();

        this.state = ReplayState.PLAYING;

        // Use provided trip data or current trip
        const trip = tripData ?? this.currentTrip;
        if ( trip === null ) {
            console.error( "[TripReplayService] No trip data to replay" );

            return;
        }

        // Check if we have data
        if ( trip.gpsData.length === 0 ) {
            console.error( "[TripReplayService] No GPS data to replay" );

            return;
        }

        // Reset indices
        this.currentGpsIndex = 0;
        this.currentCompassIndex = 0;

        // Start replaying GPS data
        this.gpsPlaying = true;
        this.startGpsReplay( trip );

        // Start replaying compass data
        this.compassPlaying = true;
        this.startCompassReplay( trip );

        console.log( "[TripReplayService] Started replaying trip", trip.metadata );
    }

    /**
     * Pause replay
     */
    public pause(): void {
        if ( this.state !== ReplayState.PLAYING ) {
            console.warn( "[TripReplayService] Not playing, cannot pause" );

            return;
        }

        // Clear intervals
        this.clearIntervals();

        this.state = ReplayState.PAUSED;
        console.log( "[TripReplayService] Replay paused" );
    }

    /**
     * Resume replay
     */
    public resume(): void {
        if ( this.state !== ReplayState.PAUSED ) {
            console.warn( "[TripReplayService] Not paused, cannot resume" );

            return;
        }

        if ( this.currentTrip === null ) {
            console.error( "[TripReplayService] No trip data to resume" );

            return;
        }

        // Resume replaying
        this.state = ReplayState.PLAYING;
        this.startGpsReplay( this.currentTrip );
        this.startCompassReplay( this.currentTrip );

        console.log( "[TripReplayService] Replay resumed" );
    }

    /**
     * Stop replay
     */
    public stop(): void {
        // only stop if all data has been streamed
        if ( !this.gpsPlaying && !this.compassPlaying ) {
            // Clear intervals
            this.clearIntervals();

            this.state = ReplayState.STOPPED;
            this.currentGpsIndex = 0;
            this.currentCompassIndex = 0;
            console.log( "[TripReplayService] Replay stopped, re-initializing GPS position" );

            // resume GPS data service to get current position and re-enabling the watch
            rawGpsDataService.resume();
            rawCompassDataService.resume();
        }
    }

    /**
     * Start replaying GPS data
     * @param trip - Trip data to replay
     */
    private startGpsReplay( trip: RecordedTripData ): void {
        if ( trip.gpsData.length === 0 ) {
            return;
        }

        let index = this.currentGpsIndex;

        // Schedule GPS data replay at original timestamps
        const scheduleNextGps = () => {
            const gpsData = trip.gpsData[index];

            let delay = 1;
            if ( index < trip.gpsData.length - 1 ) {
                delay = trip.gpsData[index + 1].timestamp - gpsData.timestamp;
            } else if ( index >= trip.gpsData.length || this.state !== ReplayState.PLAYING ) {
                if ( index >= trip.gpsData.length ) {
                    // Replay complete
                    console.log( "[TripReplayService] No more GPS data to stream, stopping GPS part." );
                    this.gpsPlaying = false;
                    this.stop();
                }

                return;
            }

            this.gpsIntervalId = window.setTimeout( () => {
                // Inject GPS data into service
                rawGpsDataService.injectData( gpsData );

                this.currentGpsIndex = index;
                scheduleNextGps();
            }, delay );

            index++;
        };

        scheduleNextGps();
    }

    /**
     * Start replaying compass data
     * @param trip - Trip data to replay
     */
    private startCompassReplay( trip: RecordedTripData ): void {
        if ( trip.compassData.length === 0 ) {
            return;
        }

        let index = this.currentCompassIndex;

        // Replay compass data at original timestamps
        const scheduleNextCompass = () => {
            const compassData = trip.compassData[index];
            let delay = 1;

            if ( index < trip.compassData.length - 1 ) {
                delay = trip.compassData[index + 1].timestamp - compassData.timestamp;
            } else if ( index >= trip.compassData.length || this.state !== ReplayState.PLAYING ) {
                if ( index >= trip.compassData.length ) {
                    console.log( "[TripReplayService] No more Compass data to stream, stopping Compass part." );
                    this.compassPlaying = false;
                    this.stop();
                }

                return;
            }

            this.compassIntervalId = window.setTimeout( () => {
                // Inject compass data into service
                rawCompassDataService.injectData( compassData );

                this.currentCompassIndex = index;
                scheduleNextCompass();
            }, delay );

            index++;
        };

        scheduleNextCompass();
    }

    /**
     * Clear all replay intervals
     */
    private clearIntervals(): void {
        if ( this.gpsIntervalId !== null ) {
            clearTimeout( this.gpsIntervalId );
            this.gpsIntervalId = null;
        }

        if ( this.compassIntervalId !== null ) {
            clearTimeout( this.compassIntervalId );
            this.compassIntervalId = null;
        }
    }

    /**
     * Returns approximate in-memory size of the loaded replay trip payload.
     *
     * @returns Replay state and byte estimate for diagnostics.
     */
    public getMemoryDiagnostics(): {
        "state":              string;
        "tripLoaded":         boolean;
        "gpsSampleCount":     number;
        "compassSampleCount": number;
        "estimatedBytes":     number;
    } {
        const trip = this.currentTrip;

        const estimatedBytes = trip !== null ? estimateJsonUtf8Bytes( trip ) : 0;

        return {
            "state":              String( this.state ),
            "tripLoaded":         trip !== null,
            "gpsSampleCount":     trip?.gpsData.length ?? 0,
            "compassSampleCount": trip?.compassData.length ?? 0,
            estimatedBytes,
        };
    }

    /**
     * Get current replay state
     * @returns Current replay state
     */
    public getState(): ReplayState {
        return this.state;
    }

    /**
     * Get current trip being replayed
     * @returns Current trip data or null
     */
    public getCurrentTrip(): RecordedTripData | null {
        return this.currentTrip;
    }

}

/**
 * Export singleton instance for convenience
 */
export const tripReplayService = TripReplayService.getInstance();

